// skill-adv-dis-info.ts — client-side render of the per-skill ADV/DIS breakdown
// panel, reading from window.FateTide.casterCache (no DB ping on a cache hit).
//
// Replaces options/tools/encounter_creatures/encounter_creature_info/
// skill_adv_dis_info_inc.php. Data: caster.computed.creature.skills[<var>].adv_dis_breakdown =
// {lines:[{value,label,kind,href_id}], rolled_total}, held by the native compiler.
// Opened from the skill list
// view's ADV/DIS button (the btn-link). Each line is +1 advantage (green) or -1
// disadvantage (red); the footer shows the net stack total.

import { setActiveInfoPanel, paintActiveInfoPanel, rerenderActiveInfoPanel } from './active-panel';
import type { CachedCaster, SkillEntry, SourceRow } from './types';
// Round-3 #4 lean: stored breakdown lines may carry NO `label` — re-derived from href_id.
// ROUND-9 #5: NO `kind` either (equals the href's catalog item_type) — resolveLineKind.
// Both shared from source-line-block.ts so all sources panels stay in lockstep.
import { resolveLineLabel, resolveLineKind } from './source-line-block';

/** SKILL_CATALOG row for a skill item_id (per-request, never stale; {} off ft_boot pages). */
function skillCatalogRow(skillItemId: string): { variable_name?: string; item_name?: string } | undefined {
    return window.SKILL_CATALOG?.[skillItemId];
}

/** Find the computed.creature.skills entry for a skill item_id (skips the _meta bucket). */
function findSkillByItemId(caster: CachedCaster, skillItemId: string): SkillEntry | undefined {
    // Canonical computed.creature.skills, with a top-level fallback for pre-move cached blobs.
    const skills = (caster.computed?.creature?.skills ?? caster.skills ?? {}) as Record<string, unknown>;
    // v33 (ROUND-12 #1): entries carry no item_id — the bucket KEY (variable_name) is the
    // join key; resolve it via SKILL_CATALOG. (Stacks-bearing entries are never elided, so
    // a keyed read suffices; the item_id scan below covers pre-v33 cached blobs.)
    const v = String(skillCatalogRow(skillItemId)?.variable_name ?? '');
    if (v !== '') {
        const keyed = skills[v];
        if (keyed && typeof keyed === 'object') return keyed as SkillEntry;
    }
    for (const key of Object.keys(skills)) {
        if (key === '_meta') continue;
        const entry = skills[key] as SkillEntry | undefined;
        if (entry && typeof entry === 'object' && String(entry.item_id ?? '') === skillItemId) {
            return entry;
        }
    }
    return undefined;
}

function renderAdvDisPanel(caster: CachedCaster, skillItemId: string): string {
    const entry = findSkillByItemId(caster, skillItemId);
    // item_name left the stored entry in v22 — resolve catalog-first (entry fallback
    // covers pre-v22 cached blobs).
    const skillName = String(entry?.item_name ?? skillCatalogRow(skillItemId)?.item_name ?? '');
    const breakdown = entry?.adv_dis_breakdown ?? {};
    const lines: SourceRow[] = Array.isArray(breakdown.lines) ? breakdown.lines : [];
    const net = Number(breakdown.rolled_total ?? entry?.adv_dis_stacks ?? 0);

    const header = `<thead><tr>
        <th scope="col" colspan="2" class="table-secondary align-middle text-start">
            <span class="d-inline-flex align-items-center gap-2 flex-wrap">
                ${iconMap('adv_dis')}${iconMap(skillItemId)}<span>${escapeHtml(skillName)}</span>
            </span>
        </th>
    </tr></thead>`;

    if (lines.length === 0 && net === 0) {
        return `<table class="table border">${header}<tbody><tr><td class="text-start py-3 small" colspan="2">
            <span class="text-muted">No modifiers in this column for this skill right now.</span>
        </td></tr></tbody></table>`;
    }

    let rows = '';
    for (const ln of lines) {
        const value = Number(ln.value ?? 0);
        const kind = resolveLineKind(caster, ln) || 'passive_ability';
        const href = String(ln.href_id ?? '');
        const label = escapeHtml(String(ln.label ?? resolveLineLabel(caster, href)));
        const labelHtml = (href !== '' && checkIfValidUUID(href))
            ? `<a class="link-primary" href="${escapeHtml(BASE_URL + 'item.php?id=' + href)}" target="_blank" rel="noopener noreferrer">${label}</a>`
            : label;
        const badgeCls = value > 0 ? 'bg-success' : (value < 0 ? 'bg-danger' : 'bg-secondary');

        rows += `<div class="list-group-item d-flex justify-content-between align-items-center gap-2 flex-wrap py-2 w-100">
            <span class="d-flex align-items-center gap-2 text-start flex-grow-1 min-w-0">
                <span class="flex-shrink-0 d-inline-flex align-items-center" aria-hidden="true">${iconMap(kind, 22, 22)}</span>
                <span class="min-w-0">${labelHtml}</span>
            </span>
            <span class="badge ${badgeCls} flex-shrink-0">${value >= 0 ? '+' : ''}${value}</span>
        </div>`;
    }

    const netBadge = net !== 0 ? 'bg-warning text-dark' : 'bg-secondary';
    const footer = `<div class="list-group-item list-group-item-light d-flex align-items-center justify-content-between gap-2 py-2 w-100 fw-semibold">
        <div class="d-flex align-items-center gap-2 text-start flex-grow-1 min-w-0">
            <span class="flex-shrink-0 d-inline-flex align-items-center" aria-hidden="true">${iconMap('adv_dis', 22, 22)}</span>
            <span class="flex-shrink-0 d-inline-flex align-items-center" aria-hidden="true">${iconMap(skillItemId, 22, 22)}</span>
            <span>${escapeHtml(skillName)} Total</span>
        </div>
        <div class="d-flex align-items-center justify-content-center flex-shrink-0">
            <h3 class="mb-0 lh-1 d-flex align-items-center"><span class="badge ${netBadge}">${net >= 0 ? '+' : ''}${net}</span></h3>
        </div>
    </div>`;

    return `<table class="table border">${header}<tbody><tr><td class="text-start align-middle py-3 small" colspan="2">
        <div class="list-group list-group-flush border rounded overflow-hidden small text-start w-100 d-flex flex-column align-items-stretch">
            ${rows}${footer}
        </div>
    </td></tr></tbody></table>`;
}

let openPanel: { casterId: string; skillId: string; target: HTMLElement; first: ChildNode | null } | undefined;

/**
 * Entry point. Repointed onclick: the ADV/DIS btn-link in the skill list view
 * (_skills_inc.php). skillItemId is the Skill catalog item_id.
 */
export async function renderSkillAdvDisInfoPanel(
    casterId: string,
    skillItemId: string,
    targetId = 'div_refresh_side_info',
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) {
        return;
    }

    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }

    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
        return;
    }

    const sid = String(skillItemId ?? '');
    if (openPanel?.casterId === casterId && openPanel.skillId === sid
        && openPanel.target === target && openPanel.first === target.firstChild) {
        rerenderActiveInfoPanel(casterId);
        return;
    }
    const render = (c: CachedCaster) => renderAdvDisPanel(c, sid);
    setActiveInfoPanel(casterId, targetId, render);
    paintActiveInfoPanel(caster);
    openPanel = { casterId, skillId: sid, target, first: target.firstChild };
}
